import { useEffect, useMemo, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { BsFillCheckCircleFill, BsXCircleFill } from 'react-icons/bs';
import { testIds, getAnnouncementReadTime } from 'src/app/constants';
import LatexHTMLRenderer from 'src/app/components/LatexHTMLRenderer/LatexHTMLRenderer';
import styles from './Explanation.module.scss';
import multipleInputStyles from '../TutorChatBubble/MultipleInput/MultipleInput.module.scss';
import PinButton from '../PinButton/PinButton';
import { checkExerciseAtom, tutorStateAtom } from 'src/app/state/atoms';
import { t } from 'i18next';
import { useOffscreenLatexText } from 'src/app/hooks/useOffscreenLatexText';

type ExplanationPropTypes = {
  explanation: string;
  isCorrect?: boolean | 'full' | 'partial' | 'none';
  pinGroupId?: string | number;
  isPinned?: boolean;
  isSecondLastItem?: boolean;
  shouldFocus?: boolean;
  onFocusMessage?: () => void;
};

function Explanation(props: ExplanationPropTypes) {
  const { isCorrect, explanation, pinGroupId, isPinned, shouldFocus, onFocusMessage } = props;
  const [exerciseResponse] = useRecoilState(checkExerciseAtom);
  const [tutorState] = useRecoilState(tutorStateAtom);
  const plainExplanationContent = useOffscreenLatexText(explanation);
  const hasReleasedRef = useRef(false);

  const srOnlyText = useMemo(() => {
    let answer;
    if(typeof isCorrect === 'boolean'){
      answer = isCorrect ? 'CORRECT' : 'INCORRECT'
    } else {
      if(isCorrect === 'full')
        answer = 'CORRECT'
      else if(isCorrect === 'partial')
        answer = 'PARTIAL_CORRECT'
      else
        answer = 'INCORRECT'
    }

    // Hold the announcement until the offscreen LaTeX render has produced the
    // explanation text, otherwise the region announces the label first and then
    // re-announces everything once the text arrives.
    if (!plainExplanationContent) return '';

    return `${t(answer)} ${t('ANSWER')} ${plainExplanationContent}`
  }, [isCorrect, plainExplanationContent])

  const isPositiveResult =
    isCorrect === true || isCorrect === 'full' || isCorrect === 'partial';

  // APAI-2290: hold the next question back until the result has been announced.
  useEffect(() => {
    if (!shouldFocus || hasReleasedRef.current) return;

    const announced = plainExplanationContent || explanation;
    if (!announced) return;
    hasReleasedRef.current = true;

    const timerId = setTimeout(() => onFocusMessage?.(), getAnnouncementReadTime(announced));
    return () => clearTimeout(timerId);
  }, [shouldFocus, onFocusMessage, plainExplanationContent, explanation]);

  if (!explanation) {
    return null;
  }

  return (
    <div data-test-id={testIds.explanation_section} className={styles.explanationSection}>
      <div
        role='status'
        aria-live={props.isSecondLastItem || shouldFocus ? 'assertive' : 'off'}
        aria-atomic={props.isSecondLastItem || shouldFocus ? 'true' : 'false'}
      >
        {srOnlyText && <span className='sr-only'>{srOnlyText}</span>}
      </div>
      <div
        aria-hidden={true}
        className={`${isPositiveResult ? styles.resultCardCorrect : styles.resultCardIncorrect} ${
          isPinned ? styles.pinned : ''
        }`}
      >

        <div  className={styles.status}>
          {/* TODO: Fix the huge correct checker */}
          {typeof isCorrect === 'boolean' ? (
            <>
              {isCorrect === true && (
                <>
                  <BsFillCheckCircleFill className='text-accent-green' size={20} focusable={false}/>
                  <span className={`${styles.statusLabel} text-accent-green`}>{t('CORRECT')}</span>
                </>
              )}
              {isCorrect === false && (
                <>
                  <BsXCircleFill className='text-accent-red' size={20} focusable={false}/>
                  <span className={`${styles.statusLabel} text-accent-red`}>{t('INCORRECT')}</span>
                </>
              )}
            </>
          ) : (
            <>
              {isCorrect === 'full' && (
                <>
                  <BsFillCheckCircleFill className='text-accent-green' size={20} focusable={false}/>
                  <span className={`${styles.statusLabel} text-accent-green`}>{t('CORRECT')}</span>
                </>
              )}
              {isCorrect === 'partial' && (
                <>
                  <BsFillCheckCircleFill className='text-accent-green' size={20} focusable={false}/>
                  <span className={`${styles.statusLabel} text-accent-green`}>{t('PARTIAL_CORRECT')}</span>
                </>
              )}
              {isCorrect === 'none' && (
                <>
                  <BsXCircleFill className='text-accent-red' size={20} focusable={false}/>
                  <span className={`${styles.statusLabel} text-accent-red`}>{t('INCORRECT')}</span>
                </>
              )}
            </>
          )}
          <span className={styles.pinButton}>
            {exerciseResponse.data.enable_pinning &&
              !tutorState?.isSMEMode &&
              !tutorState.isHistoryMode && (
                <div>
                  <PinButton label={explanation} group={pinGroupId} />
                </div>
              )}
          </span>
        </div>
        {explanation && (
          <div aria-hidden={true} className={`${styles.content} ${multipleInputStyles.customizedScroll} !overflow-x-auto overflow-y-hidden`}
          >
            <LatexHTMLRenderer content={explanation} size='small' />
          </div>
        )}
      </div>
    </div>
  );
}

export default Explanation;
